import { Button, Col, Form, FormInstance, Modal, Row } from 'antd';
import React, { useState } from 'react';

import { L } from '@lib';

import { SharedSpinner } from '@components';
import styled from 'styled-components';

const SharedFormBodyWrapper = styled.div`
  flex-grow: 1;
  overflow: hidden;
  overflow-y: auto;
`;
const SharedFormFooterWrapper = styled.div`
  flex: 0 0;
`;

interface IProps {
  id?: string;
  name?: string;
  loading?: boolean;
  submitDisabled?: boolean;
  cancelDisabled?: boolean;
  editMode?: boolean;
  form: FormInstance<any>;
  layout?: 'vertical' | 'horizontal';
  initialValues?: Object;
  submitTitle?: string;
  cancelTitle?: string;
  children?: any;
  hideCancel?: boolean;
  hideButtons?: boolean;
  style?: React.CSSProperties | undefined;
  footerClassName?: string;
  wrapperFormClass?: string;
  onCancel?: (value: boolean) => void;
  onFinish?: (values: any) => Promise<void>;
  onKeyDown?: React.KeyboardEventHandler<HTMLFormElement> | undefined;
  onValuesChange?: ((changedValues: any, values: any) => void) | undefined;
}

export const SharedForm: React.FC<IProps> = ({
  id,
  name,
  form,
  style,
  loading = false,
  hideCancel = false,
  hideButtons = false,
  submitDisabled = false,
  cancelDisabled = false,
  editMode = false,
  initialValues,
  footerClassName,
  wrapperFormClass,
  submitTitle = L('Save changes'),
  cancelTitle = L('Cancel'),
  layout = 'vertical',
  onCancel,
  onFinish,
  onKeyDown,
  onValuesChange,
  children,
}) => {
  const [disable, setDisable] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleSubmit = () => {
    setDisable(true);
    const values = form.getFieldsValue();
    onFinish &&
      onFinish(values)
        .then(() => {
          setDisable(false);
        })
        .catch(() => {
          setDisable(false);
        });
    setShowConfirmModal(false);
  };

  const handleConfirmModalOpen = () => {
    if (editMode) {
      setShowConfirmModal(true);
    } else handleSubmit();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel(false);
    } else {
      form.resetFields();
    }
  };

  return (
    <SharedSpinner loading={loading}>
      <Form
        id={id}
        style={style}
        form={form}
        initialValues={initialValues}
        name={name}
        layout={layout}
        validateTrigger="onBlur"
        scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
        onFinish={handleConfirmModalOpen}
        onKeyDown={onKeyDown}
        onValuesChange={onValuesChange}
      >
        <SharedFormBodyWrapper className={wrapperFormClass}>{children}</SharedFormBodyWrapper>
        {!hideButtons && (
          <SharedFormFooterWrapper className={footerClassName}>
            <Row justify="end" gutter={10}>
              {!hideCancel && (
                <Col>
                  <Form.Item style={{ margin: 0, padding: 0 }}>
                    <Button disabled={disable || cancelDisabled || loading} onClick={handleCancel} type="default">
                      {cancelTitle}
                    </Button>
                  </Form.Item>
                </Col>
              )}
              <Col>
                <Form.Item style={{ margin: 0, padding: 0 }}>
                  <Button
                    loading={disable || loading}
                    disabled={disable || submitDisabled || loading}
                    type="primary"
                    htmlType="submit"
                  >
                    {submitTitle}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
            {editMode && (
              <Modal
                title={L('Confirm changes')}
                open={showConfirmModal}
                onCancel={() => setShowConfirmModal(false)}
                okText={L('Save changes')}
                onOk={handleSubmit}
              >
                <p>{L('Are you sure you want to save the changes?')}</p>
              </Modal>
            )}
          </SharedFormFooterWrapper>
        )}
      </Form>
    </SharedSpinner>
  );
};
